/**
 * The guest edit deadline, pure (batch-29, item R1; plan §5.4). Business days are Monday to Friday with no holiday
 * calendar; the deadline day is read in the hotel's time zone and edits are allowed through the end of that day.
 */
import { addDays, zoneOrUtc } from '../../lib/weeks';

const DAY_MS = 86_400_000;
/** 0 = Sunday … 6 = Saturday for a `YYYY-MM-DD` day. */
const weekday = (day: string): number => new Date(`${day}T00:00:00Z`).getUTCDay();

/**
 * The last day a guest may edit: `days` business days before check-in. 0 means the check-in day itself.
 * Fri Nov 13 2026 and 3 days → Tue Nov 10; Mon Nov 16 and 3 days → Wed Nov 11 (the weekend is skipped).
 */
export function businessDaysBefore(checkIn: string, days: number): string {
  let day = checkIn;
  for (let left = Math.max(0, Math.trunc(days)); left > 0; ) {
    day = addDays(day, -1);
    const w = weekday(day);
    if (w !== 0 && w !== 6) left--;
  }
  return day;
}

/** Today's date where the hotel is. An unknown or blank zone reads as UTC. */
export function dayInZone(now: Date, timeZone: string | null | undefined): string {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: zoneOrUtc(timeZone), year: 'numeric', month: '2-digit', day: '2-digit' });
  const p = Object.fromEntries(f.formatToParts(now).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

/** Edits are open through the end of the deadline day, in the hotel's time zone. */
export function editsOpen(now: Date, checkIn: string, days: number, timeZone: string | null | undefined): boolean {
  return dayInZone(now, timeZone) <= businessDaysBefore(checkIn, days);
}

/** Whole days between two `YYYY-MM-DD` days (b − a). */
export const daysBetween = (a: string, b: string): number => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS);
